
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { useCandidates } from "@/hooks/candidates";
import { AddCandidateForm } from "@/components/candidates/AddCandidateForm";
import { Candidate } from "@/types/candidate";
import { CandidatesFilterProvider } from "@/contexts/CandidatesFilterContext";
import { CandidatesContent } from "@/components/candidates/CandidatesContent";
import { CandidatesActions } from "@/components/candidates/CandidatesActions";
import { CandidatesFilterWrapper } from "@/components/candidates/CandidatesFilterWrapper";
import { CandidatesSkillsExtractor } from "@/components/candidates/CandidatesSkillsExtractor";
import { AddCandidateSelectionModal } from "@/components/candidates/AddCandidateSelectionModal";
import { CandidateFormData } from "@/utils/candidate";
import { usePersistedViewMode } from "@/utils/viewMode";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Candidates = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
	const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [prefillFormData, setPrefillFormData] = useState<CandidateFormData | null>(null);
	const [viewMode, setViewMode] = usePersistedViewMode("candidates");
	const [isProcessingLinkedIn, setIsProcessingLinkedIn] = useState(false);
	
	const { candidates, setCandidates, isLoading, fetchCandidates } = useCandidates();

	// For status, we use a fixed list
	const allStatuses = ["looking", "open", "unavailable"];
	const allSkills = CandidatesSkillsExtractor({ candidates });

	// Direct function to fetch LinkedIn data
	const fetchLinkedInData = async (linkedinUrl: string) => {
		console.log("[CandidatesPage] Direct fetch of LinkedIn data for:", linkedinUrl);
		setIsProcessingLinkedIn(true);
		
		try {
			// Show processing toast
			toast({
				title: "Processing LinkedIn Profile",
				description: "Fetching data from LinkedIn API...",
			});
			
			// Call fetch-linkedin-profile edge function directly
			const { data: profileData, error } = await supabase.functions.invoke('fetch-linkedin-profile', {
				body: { profileUrl: linkedinUrl },
				headers: { 'Content-Type': 'application/json' }
			});
			
			console.log("[CandidatesPage] Edge function response:", profileData);
			
			if (error) {
				console.error("[CandidatesPage] Edge function error:", error);
				toast({
					title: "LinkedIn Import Failed",
					description: error.message || "Failed to fetch LinkedIn data",
					variant: "destructive"
				});
				return null;
			}
			
			if (profileData && profileData.success) {
				toast({
					title: "LinkedIn Profile Imported",
					description: profileData.import?.message || "LinkedIn data retrieved successfully",
				});
				
				// If data is available, prefill the form
				if (profileData.data) {
					const formData: CandidateFormData = {
						firstName: profileData.data.firstName || "",
						lastName: profileData.data.lastName || "",
						email: profileData.data.email || "",
						phone: profileData.data.phone || "",
						role: profileData.data.role || "",
						skills: profileData.data.skills || "",
						summary: profileData.data.summary || "",
						linkedin: profileData.data.linkedin || linkedinUrl,
						cvFile: null,
						linkedinJson: profileData.data.linkedinJson
					};
					
					setPrefillFormData(formData);
					setIsAddFormModalOpen(true);
					return formData;
				}
			} else {
				toast({
					title: "LinkedIn Import Failed",
					description: "Failed to process LinkedIn data",
					variant: "destructive"
				});
			}
		} catch (error) {
			console.error("[CandidatesPage] Error fetching LinkedIn data:", error);
			toast({
				title: "LinkedIn Import Failed",
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive"
			});
		} finally {
			setIsProcessingLinkedIn(false);
		}
		
		return null;
	};

	// Check URL parameters on component mount for prefill data and modal trigger
	useEffect(() => {
		console.log("[CandidatesPage] Checking URL parameters");
		const params = new URLSearchParams(location.search);
		const create = params.get('create');
		const linkedinUrl = params.get('linkedinUrl');

		console.log('[CandidatesPage] URL Parameters detected:', {
			create,
			linkedinUrl,
		});

		if (create === 'true' && linkedinUrl) {
			console.log('[CandidatesPage] Opening add form modal and fetching LinkedIn data due to URL parameter');

			// Clean up URL parameters after using them
			navigate(location.pathname, { replace: true });

			// Fetch LinkedIn data directly
			fetchLinkedInData(linkedinUrl);
		}
	}, [location.search, navigate, location.pathname]);

	const handleCandidateAdded = (newCandidate: Candidate) => {
		setCandidates([...candidates, newCandidate]);
		setIsAddFormModalOpen(false);
		setPrefillFormData(null);
		
		// Navigate to the new candidate's detail page
		navigate(`/candidates/${newCandidate.id}`);
	};

	const handleManualCreate = () => {
		setIsSelectionModalOpen(false);
		setPrefillFormData(null);
		setIsAddFormModalOpen(true);
	};

	const handleCVUpload = (formData: CandidateFormData) => {
		setIsSelectionModalOpen(false);
		setPrefillFormData(formData);
		setIsAddFormModalOpen(true);
	};

	return (
		<CandidatesFilterProvider viewMode={viewMode} setViewMode={setViewMode}>
			<div>
				<PageHeader 
					title={t("candidates.title")}
					actions={
						<CandidatesActions 
							isGenerating={isGenerating || isProcessingLinkedIn}
							setIsGenerating={setIsGenerating}
							fetchCandidates={fetchCandidates}
							onAddCandidate={() => setIsSelectionModalOpen(true)}
						/>
					}
				/>
				
				<CandidatesFilterWrapper 
					allSkills={allSkills}
					allStatuses={allStatuses}
				/>
				
				<CandidatesContent 
					candidates={candidates}
					isLoading={isLoading || isProcessingLinkedIn}
				/>
				
				<AddCandidateSelectionModal
					isOpen={isSelectionModalOpen}
					onClose={() => setIsSelectionModalOpen(false)}
					onManualCreate={handleManualCreate}
					onCVUpload={handleCVUpload}
				/>
				
				<AddCandidateForm 
					isOpen={isAddFormModalOpen}
					onClose={() => setIsAddFormModalOpen(false)}
					onCandidateAdded={handleCandidateAdded}
					initialFormData={prefillFormData}
				/>
			</div>
		</CandidatesFilterProvider>
	);
};

export default Candidates;
